import { makeStyles } from "tss-react/mui";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useRef } from "react";
import { LayersOutlined as LayersIcon, DirectionsCarOutlined as CarIcon, Menu as MenuIcon } from "@mui/icons-material";
import ContainerButton, { OperationsMapButton } from "./common/ContainerButton";
import StyleSelector from "./common/StyleSelector";
import NavigationButtonWidget from "./common/NavigationButtonWidget";
import { useDevice } from "../../../providers/DeviceProvider";
import { useOperationsMap } from "./OperationsMapProvider";
import { useStation } from "../../../providers/StationProvider";
import OperationsMap from "./map/OperationsMap";
import ActiveTasksPanel from "./common/ActiveTasksPanel";
import { useAuth } from "@emberly/zenith-client";
import LayerSettingsPanel from "./common/LayerSettingsPanel";
import TaskDialog from "./common/TaskDialog";
import ActiveTasksToolbar from "./common/ActiveTasksToolbar";
import PopupDialog from "./common/PopupDialog";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%"
    },
    map: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%"
    },
    topLeftButton: {
      position: "absolute",
      top: theme.spacing(1.5),
      left: theme.spacing(1.5),
      zIndex: 3
    },
    topLeftButtonSlim: {
      position: "absolute",
      top: theme.spacing(1.5),
      right: `calc(${theme.spacing(3)} + 56px)`
    },
    topRightButton: {
      position: "absolute",
      top: theme.spacing(1.5),
      right: theme.spacing(1.5)
    },
    styleSelector: {
      position: "absolute",
      bottom: theme.spacing(1.5),
      left: theme.spacing(1.5)
    },
    styleSelectorTopLeftExpanded: {
      left: `calc(${theme.spacing(3)} + 390px)`
    },
    navigationWidgetSlim: {
      position: "absolute",
      bottom: theme.spacing(1.5),
      right: theme.spacing(1.5)
    },
    navigationWidget: {
      position: "absolute",
      bottom: theme.spacing(1.5),
      left: `calc(${theme.spacing(3)} + 88px)`
    },
    navigationWidgetTopLeftExpanded: {
      left: `calc(${theme.spacing(4.5)} + 88px + 390px)`
    }
  })
);

export default function OperationsMapLayout() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { slim, setSidebarOpen } = useDevice();
  const { station } = useStation();
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth();
  const [popupDialog, setPopupDialog] = useState(null);

  const {
    topLeftExpanded,
    setTopLeftExpanded,
    topRightExpanded,
    setTopRightExpanded,
    styleSelectorExpanded,
    setStyleSelectorExpanded,
    setDispatchLayerHoverId,
    style,
    setStyle,
    containerRef,
    setOperationsMap,
    operationsMap,
    openTaskDialog,
    settings,
    setLoaded,
  } = useOperationsMap();


  const stationRef = useRef(station);
  stationRef.current = station;
  const hasStation = !!station;


  useEffect(() => {
    const container = containerRef.current;

    if (!!container && hasStation) {
      let m = new OperationsMap("main", settings, container, stationRef.current, theme, t, getAccessTokenSilently);
      setOperationsMap(m);

      m.on("dispatchLayerHover", setDispatchLayerHoverId);
      m.on("dispatchLayerClick", openTaskDialog);

      m.on("popupDialog", setPopupDialog);
      m.on("loaded", setLoaded);

      return () => {
        m?.destroy();
      }
    }
  }, [hasStation, theme, t, getAccessTokenSilently, settings]);

  const onStyleSelected = useCallback((value) => {
    setStyleSelectorExpanded(false);
    setStyle(value.id)
    operationsMap?.setStyle(value);
  }, [operationsMap]);

  const openSideBar = useCallback(() => setSidebarOpen(true), [setSidebarOpen]);

  return (
    <Box className={classes.root}>

      {slim ? (
        <OperationsMapButton
          className={classes.topLeftButton}
          icon={<MenuIcon />}
          onClick={openSideBar}
        />
      ) : null}

      <ContainerButton
        className={slim ? classes.topLeftButtonSlim : classes.topLeftButton}
        icon={<LayersIcon />}
        title={t("operationsMap:layersTitle")}
        width="390px"
        expanded={topLeftExpanded}
        setExpanded={setTopLeftExpanded}
        slim={slim}
      >
        <LayerSettingsPanel />
      </ContainerButton>

      {
        !slim || !topLeftExpanded ? (
          <ContainerButton
            className={classes.topRightButton}
            icon={<CarIcon />}
            title={t("operationsMap:tasksTitle")}
            width="390px"
            right
            expanded={topRightExpanded}
            setExpanded={setTopRightExpanded}
            slim={slim}
          >
            <ActiveTasksToolbar />
            <ActiveTasksPanel />
          </ContainerButton>
        ) : null
      }

      <NavigationButtonWidget
        className={`${slim ? classes.navigationWidgetSlim : classes.navigationWidget} ${topLeftExpanded && !slim ? classes.navigationWidgetTopLeftExpanded : ""}`}
        map={operationsMap}
        slim={slim}
      />

      <StyleSelector
        className={`${classes.styleSelector} ${topLeftExpanded && !slim ? classes.styleSelectorTopLeftExpanded : ""}`}
        expanded={styleSelectorExpanded}
        setExpanded={setStyleSelectorExpanded}
        onChange={onStyleSelected}
        value={style}
        slim={slim}
      />

      <div className={classes.map} ref={containerRef}></div>

      <TaskDialog />

      {!!popupDialog ? (
        <PopupDialog
          feature={popupDialog}
          onClose={() => setPopupDialog(null)}
        />
      ) : null}


    </Box>
  );
}

